import { useCallback, useState, type MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import Popper from '@mui/material/Popper';
import type { PopoverPosition } from '@mui/material/Popover';

import { companyGeneralFeatureFlagsSelector } from '@/domain/companies/companiesSelector';
import clipboardCopy from '@/lib/clipboardCopy';
import type {
  DisplayedOcrPolygon,
  OcrBox,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/ocrGeometry';
import OcrWord from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/OcrWord';
import OcrWordsDragSource from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/OcrWordsDragSource';
import OcrWordsSelectionBox from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/OcrWordsSelectionBox';
import TranslatePopup from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/TranslatePopup';
import WordContextMenu from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/WordContextMenu';
import {
  useOcrWordsSelection,
  type OcrWordsSelection,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/useOcrWordsSelection';
import {
  OcrWordsRoot,
  OcrWordsSelectionCanvas,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/StyledComponents';

type Props = {
  pageNumber: number;
  words: ReadonlyArray<OcrBox>;
  displayedWords: ReadonlyArray<DisplayedOcrPolygon>;
  width: number;
  height: number;
  scale: number;
};

type ContextMenu = { text: string; position: PopoverPosition };

const OcrWords = ({ pageNumber, words, displayedWords, width, height, scale }: Props) => {
  const isTranslateEnabled: boolean = useSelector(companyGeneralFeatureFlagsSelector).get('is_translate_enable');
  const { canvasRef, selection, gesture } = useOcrWordsSelection(words, displayedWords);
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [dismissedTranslateFor, setDismissedTranslateFor] = useState<OcrWordsSelection | null>(null);
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);
  const [selectionBox, setSelectionBox] = useState<HTMLDivElement | null>(null);

  const isTranslateOpen = isTranslateEnabled && !!selection?.text && selection !== dismissedTranslateFor;

  const dismissTranslate = useCallback(() => setDismissedTranslateFor(selection), [selection]);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // One handler for the page's words and the selection box: a word carries its index, the box does not.
  const openContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    const handle =
      event.target instanceof Element ? event.target.closest<HTMLElement>('[data-document-drag-handle]') : null;
    if (!handle) return;
    event.preventDefault();
    const { ocrWordIndex } = handle.dataset;
    const text = ocrWordIndex === undefined ? selection?.text : displayedWords[Number(ocrWordIndex)]?.text;
    setContextMenu(text === undefined ? null : { text, position: { top: event.clientY, left: event.clientX } });
  };

  const copy = useCallback(() => {
    if (!contextMenu) return;
    clipboardCopy(contextMenu.text).then(() => console.info('Text copied!'));
    setContextMenu(null);
    gesture.clear();
  }, [contextMenu, gesture]);

  return (
    <OcrWordsRoot ref={setLayer} onContextMenu={openContextMenu}>
      <OcrWordsSelectionCanvas ref={canvasRef} width={width * scale} height={height} />
      <OcrWordsDragSource pageNumber={pageNumber} words={words} displayedWords={displayedWords} selection={selection}>
        {displayedWords.map((word, index) => (
          <OcrWord key={word.id} word={word} index={index} />
        ))}
        {selection && <OcrWordsSelectionBox ref={setSelectionBox} selection={selection} onPress={dismissTranslate} />}
      </OcrWordsDragSource>
      {isTranslateOpen && selectionBox && (
        <Popper
          open
          anchorEl={selectionBox}
          modifiers={[
            { name: 'preventOverflow', options: { boundary: layer ?? 'clippingParents' } },
            { name: 'flip', enabled: true },
          ]}
          placement="bottom-start"
        >
          <TranslatePopup text={selection.text} />
        </Popper>
      )}
      <WordContextMenu position={contextMenu?.position ?? null} onClose={closeContextMenu} onCopy={copy} />
    </OcrWordsRoot>
  );
};

export default OcrWords;
